import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import CollectionOverview from './components/CollectionOverview';
import CollectionTabs from './components/CollectionTabs';
import PerformanceChart from './components/PerformanceChart';
import RecentActivity from './components/RecentActivity';
import MarketInsights from './components/MarketInsights';
import QuickActions from './components/QuickActions';
import AuctionAlertSettings from './components/AuctionAlertSettings';
import Icon from '../../components/AppIcon';
import { CollectionsService } from '../../services/collectionsService';
import { UserService } from '../../services/userService';
import { CollectiblesService } from '../../services/collectiblesService';
import { NewsService } from '../../services/newsService';

const CollectionDashboard = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAlertSettings, setShowAlertSettings] = useState(false);

  // Dashboard data state
  const [dashboardData, setDashboardData] = useState({
    collections: [],
    totalValue: 0,
    monthlyChange: 0,
    recentActivities: [],
    marketInsights: [],
    trendingItems: [],
    performanceData: []
  });

  // Load dashboard data
  useEffect(() => {
    if (user && !authLoading) {
      loadDashboardData();
    }
  }, [user, authLoading]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        collectionsResult,
        activitiesResult,
        statsResult,
        marketInsightsResult,
        featuredCollectiblesResult
      ] = await Promise.all([
        CollectionsService?.getCollectionOverview(user?.id),
        UserService?.getUserActivities(user?.id, 10),
        CollectiblesService?.getDashboardStats(user?.id),
        NewsService?.getMarketInsights(null, 5),
        CollectiblesService?.getFeaturedCollectibles(user?.id, 4)
      ]);

      // Handle potential errors
      if (collectionsResult?.error) {
        throw collectionsResult?.error;
      }

      const collections = collectionsResult?.data || [];
      const activities = activitiesResult?.data || [];
      const stats = statsResult?.data || {};
      const insights = marketInsightsResult?.data || [];
      const featured = featuredCollectiblesResult?.data || [];

      // Calculate totals
      const totalValue = collections?.reduce((sum, collection) => 
        sum + (parseFloat(collection?.value) || 0), 0) || 0;

      // Mock performance data for chart (you can replace with real historical data)
      const performanceData = generateMockPerformanceData(totalValue);

      // Calculate monthly change (mock - you can implement real calculation)
      const monthlyChange = calculateMonthlyChange(performanceData);

      setDashboardData({
        collections,
        totalValue,
        monthlyChange,
        recentActivities: activities,
        marketInsights: insights,
        trendingItems: featured,
        performanceData
      });

    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const generateMockPerformanceData = (currentValue) => {
    const months = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const baseValue = currentValue * 0.8; // Start 20% lower
    
    return months?.map((month, index) => ({
      month,
      value: Math.round(baseValue + (currentValue - baseValue) * (index / (months?.length - 1)))
    }));
  };

  const calculateMonthlyChange = (performanceData) => {
    if (performanceData?.length < 2) return 0;
    
    const current = performanceData?.[performanceData?.length - 1]?.value || 0;
    const previous = performanceData?.[performanceData?.length - 2]?.value || 0;
    
    if (previous === 0) return 0;
    
    return parseFloat((((current - previous) / previous) * 100)?.toFixed(2));
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleQuickAdd = () => {
    window.location.href = '/add-collection';
  };

  const handleQuickAction = (actionId) => {
    console.log('Quick action:', actionId);
    // Handle different quick actions
    switch (actionId) {
      case 'bulk-update':
        // Handle bulk update
        break;
      case 'export-data':
        // Handle data export
        break;
      case 'ai-insights':
        // Handle AI insights
        break;
      default:
        break;
    }
  };

  const handleSyncData = async () => {
    await loadDashboardData();
  };

  // Show loading state while authenticating or loading data
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar 
          isCollapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
        
        <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-6 lg:p-8">
            <div className="max-w-md mx-auto text-center">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertTriangle" size={32} className="text-destructive" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Failed to Load Dashboard
              </h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <button
                onClick={loadDashboardData}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200"
              >
                Try Again
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Page Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-1 sm:mb-2">Collection Dashboard</h1>
                <p className="text-muted-foreground text-sm sm:text-base">
                  Welcome back{userProfile?.full_name ? `, ${userProfile?.full_name}` : ''}! Here's your portfolio overview.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setShowAlertSettings(!showAlertSettings)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg transition-colors duration-200 text-sm min-h-[44px] ${
                    showAlertSettings
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`}
                  title="Auction Alert Settings"
                >
                  <Icon name="Bell" size={16} />
                  <span className="hidden sm:inline">Auction Alerts</span>
                </button>
                <button className="flex items-center gap-2 px-3 py-2.5 bg-muted/50 text-muted-foreground rounded-lg hover:bg-muted transition-colors duration-200 text-sm min-h-[44px]">
                  <Icon name="Calendar" size={16} />
                  <span className="hidden sm:inline">Last 30 days</span>
                </button>
                <button 
                  onClick={handleSyncData}
                  className="flex items-center gap-2 px-3 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200 premium-hover text-sm min-h-[44px]"
                >
                  <Icon name="RefreshCw" size={16} />
                  <span className="hidden sm:inline">Sync Data</span>
                </button>
              </div>
            </div>
          </div>

          {/* Auction Alert Settings Panel */}
          {showAlertSettings && (
            <div className="mb-6 sm:mb-8">
              <AuctionAlertSettings onClose={() => setShowAlertSettings(false)} />
            </div>
          )}

          {/* Collection Overview */}
          <CollectionOverview 
            collections={dashboardData?.collections}
            totalValue={dashboardData?.totalValue}
            monthlyChange={dashboardData?.monthlyChange}
          />

          {/* Collection Tabs */}
          <CollectionTabs
            collections={dashboardData?.collections}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            onQuickAdd={handleQuickAdd}
          />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 sm:gap-8">
            {/* Left Column - Charts and Activity */}
            <div className="xl:col-span-2 space-y-6 sm:space-y-8">
              <PerformanceChart 
                data={dashboardData?.performanceData}
                collections={dashboardData?.collections}
              />
              
              <RecentActivity activities={dashboardData?.recentActivities} />
            </div>

            {/* Right Column - Insights and Actions */}
            <div className="space-y-6 sm:space-y-8">
              <MarketInsights 
                insights={dashboardData?.marketInsights}
                trendingItems={dashboardData?.trendingItems}
              />
              
              <QuickActions onAction={handleQuickAction} />
            </div>
          </div>

          {/* Bottom Section - Additional Widgets */}
          <div className="mt-6 sm:mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {/* Achievement Progress */}
            <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-card-foreground mb-4 sm:mb-6">Achievement Progress</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Trophy" size={18} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-sm font-medium text-card-foreground">Collection Master</h4>
                      <p className="text-xs text-muted-foreground">Add 100 items to your collection</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-medium text-card-foreground">
                      {dashboardData?.collections?.reduce((sum, c) => sum + (c?.items || 0), 0)}/100
                    </div>
                    <div className="w-16 sm:w-20 h-2 bg-muted rounded-full mt-1">
                      <div 
                        className="h-full bg-primary rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.min(100, (dashboardData?.collections?.reduce((sum, c) => sum + (c?.items || 0), 0) / 100) * 100)}%`
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-success/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="TrendingUp" size={18} className="text-success" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-sm font-medium text-card-foreground">Portfolio Growth</h4>
                      <p className="text-xs text-muted-foreground">Achieve 20% portfolio growth</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-medium text-success">
                      {dashboardData?.monthlyChange > 20 ? 'Completed!' : `${dashboardData?.monthlyChange?.toFixed(2)}/20%`}
                    </div>
                    <div className="w-16 sm:w-20 h-2 bg-muted rounded-full mt-1">
                      <div 
                        className="h-full bg-success rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.min(100, Math.max(0, dashboardData?.monthlyChange / 20) * 100)}%`
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Community Highlights */}
            <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-card-foreground mb-4 sm:mb-6">Community Highlights</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-muted/20 rounded-lg">
                  <div className="w-8 h-8 bg-gradient-to-br from-primary to-warning rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="User" size={16} className="text-primary-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-card-foreground">Welcome to Crazy Rare Club!</p>
                    <p className="text-xs text-muted-foreground">Start building your collection portfolio</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 p-3 bg-muted/20 rounded-lg">
                  <div className="w-8 h-8 bg-conversion-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Users" size={16} className="text-conversion-accent" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-card-foreground">Community Growth</p>
                    <p className="text-xs text-muted-foreground">Join thousands of collectors worldwide</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CollectionDashboard;